// Statuts d'une station (cadrage 3.2, D-022) et échéances déclaratives.
// Par produit : disponible ; non distribuable (motif obligatoire) ; rupture confirmée ; information non
// actualisée (dernière déclaration périmée). Un stock positif avec une pompe indisponible s'affiche « non
// distribuable », jamais « disponible ». Au niveau station : ouverte, fermée, fermée temporairement avec motif.

import { evaluerFraicheur, type SeuilsFraicheur } from '../regles/fraicheur';
import { SEUILS_FRAICHEUR_DEFAUT } from '../constantes';
import { compare } from './decimal-outils';

export const STATUTS_STATION = ['ouverte', 'fermee', 'fermee_temporairement'] as const;
export type StatutStation = (typeof STATUTS_STATION)[number];

export const STATUTS_PRODUIT_DECLARES = ['disponible', 'non_distribuable', 'rupture_confirmee'] as const;
export type StatutProduitDeclare = (typeof STATUTS_PRODUIT_DECLARES)[number];

export const MOTIFS_NON_DISTRIBUABLE = ['pompe_indisponible', 'immobilisation', 'fermeture', 'personnel_absent', 'autre'] as const;
export type MotifNonDistribuable = (typeof MOTIFS_NON_DISTRIBUABLE)[number];

export type StatutProduitAffiche = StatutProduitDeclare | 'information_non_actualisee';

export const LIBELLES_STATUT_PRODUIT: Record<StatutProduitAffiche, string> = {
  disponible: 'Disponible à la vente',
  non_distribuable: 'En stock non distribuable',
  rupture_confirmee: 'Rupture confirmée',
  information_non_actualisee: 'Information non actualisée',
};

export const LIBELLES_STATUT_STATION: Record<StatutStation, string> = {
  ouverte: 'Ouverte',
  fermee: 'Fermée',
  fermee_temporairement: 'Fermée temporairement',
};

export const LIBELLES_MOTIF_NON_DISTRIBUABLE: Record<MotifNonDistribuable, string> = {
  pompe_indisponible: 'Pompe indisponible',
  immobilisation: 'Immobilisation',
  fermeture: 'Fermeture',
  personnel_absent: 'Personnel absent',
  autre: 'Autre',
};

export interface EntreeStatutProduit {
  statutStation: StatutStation | null;
  statutDeclare: StatutProduitDeclare | null;
  motifDeclare?: string | null;
  /** Stock utilisable déclaré (volume brut moins fond de cuve) ; null si inconnu. */
  stockUtilisable: string | null;
  horodatageDeclaration: string | Date | null;
  maintenant?: Date;
  seuils?: SeuilsFraicheur;
}

export interface StatutProduitCalcule {
  statut: StatutProduitAffiche;
  motif: string | null;
}

export function calculerStatutProduit(e: EntreeStatutProduit): StatutProduitCalcule {
  const fraicheur = evaluerFraicheur(e.horodatageDeclaration, e.maintenant ?? new Date(), e.seuils ?? SEUILS_FRAICHEUR_DEFAUT);
  if (fraicheur === 'perimee' || e.statutDeclare === null) {
    return { statut: 'information_non_actualisee', motif: e.horodatageDeclaration ? 'Dernière déclaration périmée' : 'Aucune déclaration' };
  }
  if (e.statutDeclare === 'rupture_confirmee') return { statut: 'rupture_confirmee', motif: e.motifDeclare ?? null };
  const stockPositif = e.stockUtilisable !== null && compare(e.stockUtilisable, '0') > 0;
  if (e.statutStation === 'fermee' || e.statutStation === 'fermee_temporairement') {
    return { statut: 'non_distribuable', motif: e.statutStation === 'fermee' ? 'Station fermée' : 'Station fermée temporairement' };
  }
  if (e.statutDeclare === 'non_distribuable') return { statut: 'non_distribuable', motif: e.motifDeclare ?? 'Motif non précisé' };
  if (e.stockUtilisable !== null && !stockPositif) {
    return { statut: 'rupture_confirmee', motif: 'Stock utilisable nul ou sous le fond de cuve' };
  }
  return { statut: 'disponible', motif: null };
}

// ---------------------------------------------------------------------------------------------------------
// Échéances déclaratives (D-022) : stations, stock et statut chaque jour avant 10 h ; dépôts, jaugeage
// quotidien ; SOGARA, disponibilités hebdomadaires. Les heures limites sont exprimées en heure du Gabon (UTC+1).

export interface RegleEcheance {
  frequence: 'quotidienne' | 'hebdomadaire';
  /** Heure limite locale (0 à 23). */
  heureLimite: number;
  /** Jour de la semaine pour une échéance hebdomadaire (1 = lundi ... 7 = dimanche). */
  jourSemaine?: number;
  /** Décalage horaire local par rapport à UTC, en heures (UTC+1 au Gabon). */
  decalageUtcHeures?: number;
}

export const REGLES_ECHEANCE_DEFAUT: Record<string, RegleEcheance> = {
  station: { frequence: 'quotidienne', heureLimite: 10 },
  depot: { frequence: 'quotidienne', heureLimite: 10 },
  centre_emplissage: { frequence: 'quotidienne', heureLimite: 10 },
  raffinerie: { frequence: 'hebdomadaire', heureLimite: 12, jourSemaine: 1 },
};

/** Échéance (instant UTC) de la période contenant ou suivant immédiatement `instant`. */
export function prochaineEcheance(instant: Date, regle: RegleEcheance): Date {
  const decalage = (regle.decalageUtcHeures ?? 1) * 3_600_000;
  const local = new Date(instant.getTime() + decalage);
  const candidat = new Date(Date.UTC(local.getUTCFullYear(), local.getUTCMonth(), local.getUTCDate(), regle.heureLimite));
  if (regle.frequence === 'hebdomadaire') {
    const jour = ((candidat.getUTCDay() + 6) % 7) + 1;
    const cible = regle.jourSemaine ?? 1;
    candidat.setUTCDate(candidat.getUTCDate() + ((cible - jour + 7) % 7));
  }
  if (candidat.getTime() < local.getTime()) candidat.setUTCDate(candidat.getUTCDate() + (regle.frequence === 'hebdomadaire' ? 7 : 1));
  return new Date(candidat.getTime() - decalage);
}

/**
 * Nombre d'échéances manquées depuis la dernière déclaration : une déclaration couvre l'échéance qui la suit
 * immédiatement ; toute échéance ultérieure atteinte sans nouvelle déclaration est manquée. Sans aucune
 * déclaration, toutes les échéances depuis `depuis` sont comptées.
 */
export function echeancesManquees(derniere: Date | null, maintenant: Date, regle: RegleEcheance, depuis?: Date): { nombre: number; echeances: Date[] } {
  const periodeMs = (regle.frequence === 'hebdomadaire' ? 7 : 1) * 86_400_000;
  const echeances: Date[] = [];
  let courante: Date;
  if (derniere) {
    const couverte = prochaineEcheance(derniere, regle);
    courante = new Date(couverte.getTime() + periodeMs);
  } else {
    courante = prochaineEcheance(depuis ?? new Date(maintenant.getTime() - 30 * periodeMs), regle);
  }
  let garde = 0;
  while (courante.getTime() <= maintenant.getTime() && garde++ < 400) {
    echeances.push(courante);
    courante = new Date(courante.getTime() + periodeMs);
  }
  return { nombre: echeances.length, echeances };
}
